import { useState, useMemo } from 'react';
import { ChevronDown, ChevronRight, FileText, Folder, FolderOpen, Search } from 'lucide-react';
import type { TreeNode } from '@/hooks/useObsidian';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface VaultTreeProps {
  root: TreeNode | undefined;
  currentPath: string | null;
  onOpenFile: (path: string) => void;
}

export function VaultTree({ root, currentPath, onOpenFile }: VaultTreeProps): JSX.Element {
  const [filter, setFilter] = useState('');

  const filteredRoot = useMemo(() => {
    if (!root || !filter.trim()) return root;
    return filterTree(root, filter.toLowerCase());
  }, [root, filter]);

  return (
    <div className="flex flex-col h-full">
      <div className="p-2 border-b border-border">
        <div className="relative">
          <Search className="h-3 w-3 absolute left-2 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            placeholder="Filtrar arquivos…"
            className="pl-7 h-7 text-xs"
          />
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-1 text-sm">
        {filteredRoot?.children?.map((child) => (
          <Node
            key={child.path}
            node={child}
            depth={0}
            currentPath={currentPath}
            onOpenFile={onOpenFile}
            forceOpen={filter.trim().length > 0}
          />
        ))}
        {filter && filteredRoot?.children?.length === 0 && (
          <div className="px-3 py-4 text-xs text-muted-foreground text-center">
            Nada encontrado pra "{filter}"
          </div>
        )}
      </div>
    </div>
  );
}

function filterTree(node: TreeNode, filter: string): TreeNode | null {
  if (node.type === 'file') {
    return node.name.toLowerCase().includes(filter) ? node : null;
  }
  const children = (node.children ?? [])
    .map((c) => filterTree(c, filter))
    .filter((c): c is TreeNode => c !== null);
  if (children.length === 0) return null;
  return { ...node, children };
}

interface NodeProps {
  node: TreeNode;
  depth: number;
  currentPath: string | null;
  onOpenFile: (path: string) => void;
  forceOpen: boolean;
}

function Node({ node, depth, currentPath, onOpenFile, forceOpen }: NodeProps): JSX.Element {
  const [open, setOpen] = useState(depth === 0);

  if (node.type === 'file') {
    const active = currentPath === node.path;
    return (
      <button
        type="button"
        onClick={() => onOpenFile(node.path)}
        className={cn(
          'w-full flex items-center gap-1.5 pr-2 py-1 transition-colors text-left',
          active ? 'bg-accent text-foreground' : 'hover:bg-accent/50 text-muted-foreground hover:text-foreground',
        )}
        style={{ paddingLeft: `${8 + depth * 12}px` }}
      >
        <FileText className="h-3.5 w-3.5 flex-shrink-0" />
        <span className="truncate text-xs">{node.name.replace(/\.md$/, '')}</span>
      </button>
    );
  }

  const isOpen = forceOpen || open;
  const childCount = node.children?.length ?? 0;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center gap-1 pr-2 py-1 hover:bg-accent/50 text-muted-foreground hover:text-foreground transition-colors"
        style={{ paddingLeft: `${8 + depth * 12}px` }}
      >
        {isOpen ? (
          <ChevronDown className="h-3 w-3 flex-shrink-0" />
        ) : (
          <ChevronRight className="h-3 w-3 flex-shrink-0" />
        )}
        {isOpen ? (
          <FolderOpen className="h-3.5 w-3.5 flex-shrink-0" />
        ) : (
          <Folder className="h-3.5 w-3.5 flex-shrink-0" />
        )}
        <span className="truncate text-xs font-medium">{node.name}</span>
        <span className="ml-auto text-[10px] text-muted-foreground">{childCount}</span>
      </button>
      {isOpen &&
        node.children?.map((child) => (
          <Node
            key={child.path}
            node={child}
            depth={depth + 1}
            currentPath={currentPath}
            onOpenFile={onOpenFile}
            forceOpen={forceOpen}
          />
        ))}
    </>
  );
}
